<template>
    <div class="w-100 align-self-start">
        <v-card flat color="transparent">
            <v-card-title>
                <span class="secondary--text">Integrations</span>
            </v-card-title>
            <v-card-text>
                <div class="d-flex align-center">
                    <v-btn color="primary" class="mr-2" :disabled="actionInProgress || bootstrapping || isBootstrapTaskRunning" @click="openBootstrapDialog">
                        <v-icon left>mdi-plus</v-icon>
                        Provider
                    </v-btn>

                    <v-spacer></v-spacer>

                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense
                        clearable
                        placeholder="Search for an integration..."
                        class="mr-2"></v-text-field>
                    <v-btn icon :loading="loading" @click="fetchCloudAccount">
                        <v-icon>refresh</v-icon>
                    </v-btn>
                </div>

                <v-alert v-if="error" type="error" text dismissible class="mt-4 mb-0" @input="error = ''">{{ error }}</v-alert>
                <v-data-table
                    :headers="headers"
                    :items="displayedCloudAccounts"
                    :search="search"
                    :loading="loading"
                    hide-default-footer
                    class="mt-4"
                    no-data-text="No integration is connected to this resource pool.">
                    <template v-slot:[`item.cloud_credentials_expiration`]="{ item }">
                        <span>{{ formatCredentialsExpiration(item) }}</span>
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        <v-tooltip bottom :disabled="!shouldShowLongTaskTooltip(item)" max-width="300">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip small outlined label class="d-inline-flex align-center" v-bind="attrs" v-on="on">
                                    <span class="status-dot mr-2" :class="`${cloudAccountStatusColor(item)}--text`"></span>
                                    <span>{{ cloudAccountStatusText(item) }}</span>
                                </v-chip>
                            </template>
                            <span>{{ longTaskTooltipText(item) }}</span>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="text-right">
                            <v-menu left offset-x nudge-top="10" :disabled="isActionsDropdownDisabled">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        v-bind="attrs"
                                        v-on="on"
                                        text
                                        color="secondary"
                                        small
                                        :disabled="isActionsDropdownDisabled"
                                        :loading="actionInProgress">
                                        <v-icon>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list nav dense>
                                    <v-list-item :disabled="!item.tcid" @click="reconcileCloudAccount(item)">
                                        <v-list-item-icon><v-icon>mdi-sync</v-icon></v-list-item-icon>
                                        <v-list-item-title class="mr-4">Reconcile</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item :disabled="!item.tcid" @click="refreshCloudAccountResources(item)">
                                        <v-list-item-icon><v-icon>mdi-refresh</v-icon></v-list-item-icon>
                                        <v-list-item-title class="mr-4">Refresh resources</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item :disabled="!item.tcid || !canUpdateCredentials(item)" @click="openCredentialsDialog(item)">
                                        <v-list-item-icon><v-icon>mdi-key-change</v-icon></v-list-item-icon>
                                        <v-list-item-title class="mr-4">Update credentials…</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item :disabled="!item.tcid" @click="confirmCloudAccountAction(item, 'unlink')">
                                        <v-list-item-icon><v-icon color="warning">mdi-link-off</v-icon></v-list-item-icon>
                                        <v-list-item-title class="warning--text mr-4">Unlink</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item
                                        :disabled="!item.tcid || isDeleteTaskInProgress(item) || isCloudCredentialsExpired(item)"
                                        @click="confirmCloudAccountAction(item, 'delete')">
                                        <v-list-item-icon><v-icon color="error">mdi-trash-can-outline</v-icon></v-list-item-icon>
                                        <v-list-item-title class="error--text mr-4">Delete</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="bootstrapDialog" max-width="640px" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start">
                    <span class="secondary--text">Link provider</span>
                    <v-spacer></v-spacer>
                    <v-btn icon :disabled="bootstrapping" @click="closeBootstrapDialog"><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="bootstrapForm" v-model="bootstrapFormValid" @submit.prevent="bootstrapCloudCluster">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="bootstrapSettings.cloudProvider"
                                    :items="cloudProviderOptions"
                                    item-text="display_label"
                                    item-value="cloud_provider"
                                    label="Provider"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :loading="loadingCloudProviders"
                                    :disabled="bootstrapping || loadingCloudProviders"
                                    no-data-text="No providers available."></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="bootstrapSettings.cloudRegion"
                                    :items="cloudRegionOptions"
                                    label="Region"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :loading="loadingCloudProviders"
                                    :disabled="bootstrapping || loadingCloudProviders || !bootstrapSettings.cloudProvider"
                                    no-data-text="No regions available."></v-select>
                            </v-col>
                        </v-row>
                        <v-text-field
                            v-model="bootstrapCredentials.id"
                            label="Credential ID"
                            :rules="[required]"
                            outlined
                            dense
                            :disabled="bootstrapping"></v-text-field>
                        <v-text-field
                            v-model="bootstrapCredentials.secret"
                            label="Credential secret"
                            :rules="[required]"
                            type="password"
                            outlined
                            dense
                            :disabled="bootstrapping"></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text :loading="bootstrapping" :disabled="!bootstrapFormValid" @click="bootstrapCloudCluster">Link</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="credentialsDialog" max-width="640px" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start">
                    <span class="secondary--text">Update credentials</span>
                    <v-spacer></v-spacer>
                    <v-btn icon :disabled="updatingCredentials" @click="closeCredentialsDialog"><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="credentialsForm" v-model="credentialsFormValid" @submit.prevent="updateCredentials">
                        <v-text-field
                            v-model="credentialsForm.id"
                            label="Credential ID"
                            :rules="[required]"
                            outlined
                            dense
                            :disabled="updatingCredentials"></v-text-field>
                        <v-text-field
                            v-model="credentialsForm.secret"
                            label="Credential secret"
                            :rules="[required]"
                            type="password"
                            outlined
                            dense
                            :disabled="updatingCredentials"></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text :loading="updatingCredentials" :disabled="!credentialsFormValid" @click="updateCredentials">Update</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import eventBus from '@/components/EventBus'
import { fetchTask } from '@/apis'
import { enumsData } from '@/mixins/enums'

// Resumable bootstrapper stages in execution order; keys match
// BOOTSTRAP_STAGES in nv-backend app/core/tenant_cluster.py.
const BOOTSTRAP_STAGES = [
    { key: 'talos_image', label: 'Node images' },
    { key: 'cluster', label: 'Cluster' },
    { key: 'workload_infra', label: 'Workload infrastructure' },
    { key: 'ingress_prereqs', label: 'Ingress prerequisites' },
    { key: 'storage', label: 'Storage' },
    { key: 'linkerd', label: 'Service mesh' },
    { key: 'ingress_dns', label: 'Ingress and DNS' },
    { key: 'gpu', label: 'GPU' },
    { key: 'verification', label: 'Verification' }
]
const BOOTSTRAP_POLL_INTERVAL_MS = 15000
const CREDENTIALS_EXPIRY_CLOCK_INTERVAL_MS = 30000
// LockModeType.DELETING in nv-backend app/model/mixins/lockable.py; the backend holds it for the whole teardown.
const LOCK_MODE_DELETING = 0
// User-facing messages for backend tenant-cluster `code` values (nv-backend
// app/core/tenant_cluster.py, app/controller/tenant_cluster_controller.py).
// Raw backend `debug` text is never shown: it names internal concepts. Say
// "cloud provider", never "OpenStack".
const INTEGRATION_ERROR_MESSAGES = {
    tenant_cluster_bootstrap_job_failed: 'Integration could not be created.',
    tenant_cluster_bootstrap_job_timeout: 'Integration creation timed out. Try again.',
    tenant_cluster_cloud_project_in_use: 'This cloud provider project already has an integration. Each project supports only one integration.',
    tenant_cluster_cloud_project_unknown:
        'The cloud provider project could not be determined from this credential, so the integration was refused. Check the credential and try again.',
    tenant_cluster_cloud_project_unresolved:
        'Another integration still needs its cloud provider project resolved. Reconcile existing integrations first, then try again.',
    tenant_cluster_cloud_project_mismatch: 'This credential belongs to a different cloud provider project than the integration was created with.',
    tenant_cluster_cloud_project_unverifiable:
        'The cloud provider project could not be determined from this credential, so the update was refused. Check the credential and try again.',
    tenant_cluster_in_use: 'This integration cannot be deleted because it still has running applications. Stop them first, then try again.',
    tenant_cluster_credentials_expired: 'The cloud provider credentials for this integration have expired. Update the credentials, then try again.',
    tenant_cluster_credentials_invalid: 'The cloud provider rejected this credential. Check the credential ID and secret, then try again.',
    tenant_cluster_credentials_unverifiable: 'The cloud provider credential could not be verified. Check the credential and try again.',
    tenant_cluster_quota_insufficient:
        'The cloud provider project does not have enough free quota to create this integration. Increase the quota and try again.',
    tenant_cluster_credentials_forbidden: 'Only the integration creator can update its credentials.',
    tenant_cluster_credentials_already_installed: 'This integration already has credentials.',
    tenant_cluster_credentials_state_invalid: 'This integration is in an inconsistent state. Contact support.',
    tenant_cluster_credentials_missing: 'This integration has no stored cloud provider credentials.',
    tenant_openstack_credentials_missing: 'This integration has no stored cloud provider credentials.',
    tenant_cluster_bootstrap_in_progress: 'An operation is already running for this integration. Wait for it to finish, then try again.',
    object_already_locked: 'An operation is already running for this integration. Wait for it to finish, then try again.',
    tenant_cluster_already_exists: 'This account already has an integration.',
    tenant_cluster_changed_during_resync: 'The integration changed while resources were being refreshed. Try again.',
    tenant_cluster_not_ready: 'This integration is not ready yet. Try again later.',
    tenant_cluster_not_found: 'This integration no longer exists.',
    tenant_cluster_credentials_rollback_failed: 'The credential update failed and could not be fully reverted. Contact support.',
    tenant_cluster_credentials_storage_invalid: 'The stored credentials cannot be updated safely. Contact support.',
    tenant_cluster_credentials_managed_externally: 'This integration\u2019s credentials are managed externally and cannot be updated here.',
    tenant_cluster_cr_patch_failed: 'The credential update could not be applied to the integration. Try again.',
    tenant_cluster_cr_not_found: 'The integration\u2019s cloud resources were not found. Try again.',
    tenant_cluster_bootstrap_job_stale: 'A previous integration creation attempt needs cleanup before retrying. Contact support.',
    tenant_cluster_bootstrap_job_conflict: 'A conflicting creation job exists for this integration. Contact support.',
    tenant_cluster_bootstrap_job_delete_timeout: 'Integration creation cleanup timed out. Try again.',
    tenant_cluster_bootstrap_not_current: 'This operation was superseded by a newer one. Check the current status.',
    tenant_cluster_cr_delete_timeout: 'Integration deletion timed out while cloud resources were being reclaimed. Check the status and try again.',
    tenant_cluster_name_taken: 'An integration with this name already exists.'
}

export default {
    name: 'CloudAccountsPage',
    mixins: [enumsData],
    data() {
        return {
            search: '',
            loading: false,
            loadingCloudProviders: false,
            bootstrapping: false,
            actionInProgress: false,
            pendingCloudAccountAction: null,
            globalDialogHash: `cloud-account-action-${this._uid}`,
            error: '',
            cloudAccounts: [],
            bootstrapTask: null,
            deleteTasks: {},
            bootstrapPollTimer: null,
            // Reactive clock: Date.now() alone is not tracked by Vue, so an open page
            // would never re-evaluate isCloudCredentialsExpired once a credential lapses.
            nowMs: Date.now(),
            expiryClockTimer: null,
            bootstrapDialog: false,
            credentialsDialog: false,
            updatingCredentials: false,
            credentialsFormValid: false,
            credentialsTarget: null,
            credentialsForm: { id: '', secret: '' },
            bootstrapFormValid: false,
            cloudProviders: [],
            bootstrapSettings: {
                cloudProvider: null,
                cloudRegion: null
            },
            bootstrapCredentials: {
                id: '',
                secret: ''
            },
            headers: [
                { text: 'Provider', value: 'cloud_provider' },
                { text: 'Region', value: 'cloud_region' },
                { text: 'Deployment', value: 'cluster_name' },
                { text: 'Credentials expire', value: 'cloud_credentials_expiration' },
                { text: 'Status', value: 'status', sortable: false },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false }
            ]
        }
    },
    computed: {
        resourcePoolId() {
            return Number(this.$route.params.rpid)
        },
        cloudProviderOptions() {
            return this.cloudProviders
        },
        cloudRegionOptions() {
            if (!this.bootstrapSettings.cloudProvider) {
                return []
            }
            const selectedProvider = this.cloudProviders.find(provider => provider.cloud_provider === this.bootstrapSettings.cloudProvider)
            return selectedProvider?.regions || []
        },
        isBootstrapTaskRunning() {
            return ['Scheduled', 'Linking'].includes(this.bootstrapTask?.status)
        },
        hasDeleteTaskInProgress() {
            return Object.keys(this.deleteTasks).some(taskKey => {
                const status = this.deleteTasks[taskKey]?.status
                return status === 'Scheduled' || status === 'Deleting'
            })
        },
        hasServerBootstrapRunning() {
            return this.cloudAccounts.some(cloudAccount => this.serverBootstrapStatus(cloudAccount) === 'Linking')
        },
        hasServerDeleteRunning() {
            return this.cloudAccounts.some(cloudAccount => this.isServerDeleting(cloudAccount))
        },
        shouldPollCloudAccounts() {
            return this.hasServerBootstrapRunning || this.hasServerDeleteRunning
        },
        isActionsDropdownDisabled() {
            return (
                this.actionInProgress ||
                this.isBootstrapTaskRunning ||
                this.hasDeleteTaskInProgress ||
                this.hasServerBootstrapRunning ||
                this.hasServerDeleteRunning
            )
        },
        displayedCloudAccounts() {
            const cloudAccounts = Array.isArray(this.cloudAccounts) ? this.cloudAccounts : []
            if (!this.bootstrapTask || cloudAccounts.length > 0) {
                return cloudAccounts
            }

            return [
                {
                    tcid: null,
                    cloud_provider: this.bootstrapTask.cloudProvider,
                    cloud_region: this.bootstrapTask.cloudRegion,
                    cluster_name: this.bootstrapTask.status === 'Failed' ? 'Provisioning failed' : 'Provisioning...',
                    _pendingBootstrap: true
                },
                ...cloudAccounts
            ]
        }
    },
    created() {
        eventBus.$on(`global-dialog-accept-${this.globalDialogHash}`, this.executePendingCloudAccountAction)
        this.expiryClockTimer = setInterval(() => {
            this.nowMs = Date.now()
        }, CREDENTIALS_EXPIRY_CLOCK_INTERVAL_MS)
    },
    beforeDestroy() {
        eventBus.$off(`global-dialog-accept-${this.globalDialogHash}`, this.executePendingCloudAccountAction)
        clearInterval(this.bootstrapPollTimer)
        clearInterval(this.expiryClockTimer)
    },
    methods: {
        required(value) {
            return Boolean(String(value || '').trim()) || 'Required'
        },
        bootstrapErrorMessage(errorLike, fallbackMessage = 'Integration creation failed.') {
            const code = this.integrationErrorCode(errorLike)
            if (code) {
                return INTEGRATION_ERROR_MESSAGES[code]
            }

            const message =
                errorLike?.response?.data?.reason ||
                errorLike?.response?.data?.debug ||
                errorLike?.reason ||
                errorLike?.debug ||
                this.nonGenericMessage(typeof errorLike === 'string' ? errorLike : errorLike?.message)
            if (typeof message === 'string' && message.trim()) {
                return message
            }

            return fallbackMessage
        },
        // Generic axios status text ("Request failed with status code 410")
        // carries no information: fall back to the caller message instead.
        nonGenericMessage(message) {
            if (typeof message !== 'string') {
                return null
            }
            return /^Request failed with status code \d+$/.test(message.trim()) ? null : message
        },
        // Backend `code` from an axios error, a task object (failed async tasks
        // carry the code in `result` / `meta_data.error_code`), or a bare code
        // string. Returns null when the code has no user-facing message.
        integrationErrorCode(errorLike) {
            if (typeof errorLike === 'string') {
                return INTEGRATION_ERROR_MESSAGES[errorLike] ? errorLike : null
            }
            if (!errorLike || typeof errorLike !== 'object') {
                return null
            }
            const data = errorLike.response?.data || errorLike.data || {}
            const candidates = [
                data.code,
                errorLike.code,
                data.meta_data?.error_code,
                errorLike.meta_data?.error_code,
                data.result,
                errorLike.result,
                errorLike.response?.data?.result
            ]
            return candidates.find(code => typeof code === 'string' && INTEGRATION_ERROR_MESSAGES[code]) || null
        },
        credentialErrorMessage(errorLike) {
            return this.bootstrapErrorMessage(errorLike, 'Could not update credentials.')
        },
        isBootstrapTargetAccount(cloudAccount) {
            if (!this.bootstrapTask || !cloudAccount) {
                return false
            }
            const provider = String(cloudAccount.cloud_provider || '')
                .trim()
                .toLowerCase()
            const region = String(cloudAccount.cloud_region || '')
                .trim()
                .toLowerCase()
            const bootstrapProvider = String(this.bootstrapTask.cloudProvider || '')
                .trim()
                .toLowerCase()
            const bootstrapRegion = String(this.bootstrapTask.cloudRegion || '')
                .trim()
                .toLowerCase()

            return provider === bootstrapProvider && region === bootstrapRegion
        },
        isServerDeleting(cloudAccount) {
            return cloudAccount?.lock_mode === LOCK_MODE_DELETING
        },
        isDeleteTaskInProgress(cloudAccount) {
            if (!cloudAccount?.tcid) {
                return false
            }
            if (this.isServerDeleting(cloudAccount)) {
                return true
            }
            const status = this.deleteTasks[String(cloudAccount.tcid)]?.status
            return status === 'Scheduled' || status === 'Deleting'
        },
        taskCompletedSuccessfully(taskResult) {
            const status = taskResult?.data?.status
            if (Number(status) === 4) {
                return true
            }
            const normalizedStatus = String(status || '')
                .trim()
                .toUpperCase()
            return ['DONE', 'COMPLETED', 'SUCCESS', 'SUCCEEDED'].includes(normalizedStatus)
        },
        serverBootstrapStatus(cloudAccount) {
            const status = cloudAccount?.bootstrap_task_status
            if (status == null) return null
            const { CREATED, QUEUED, RUNNING, FAILED, CONTAINS_ERRORS, REJECTED, CANCELLED } = this.taskStatusTypes
            if ([CREATED, QUEUED, RUNNING].some(type => type.value === status)) return 'Linking'
            if ([FAILED, CONTAINS_ERRORS, REJECTED, CANCELLED].some(type => type.value === status)) return 'Failed'
            return null
        },
        // Stage the bootstrapper is running (or failed in): the one after the last completed stage.
        currentBootstrapStage(cloudAccount) {
            const index = BOOTSTRAP_STAGES.findIndex(stage => stage.key === cloudAccount?.bootstrap_stage) + 1
            if (index >= BOOTSTRAP_STAGES.length) return null
            return { number: index + 1, total: BOOTSTRAP_STAGES.length, label: BOOTSTRAP_STAGES[index].label }
        },
        cloudAccountStatus(cloudAccount) {
            const deleteStatus = cloudAccount?.tcid ? this.deleteTasks[String(cloudAccount.tcid)]?.status : null
            if (deleteStatus) {
                return deleteStatus
            }

            if (this.isServerDeleting(cloudAccount)) {
                return 'Deleting'
            }

            const serverStatus = this.serverBootstrapStatus(cloudAccount)
            if (serverStatus) {
                return serverStatus
            }

            if (cloudAccount?._pendingBootstrap && this.bootstrapTask?.status) {
                return this.bootstrapTask.status
            }

            if (this.isBootstrapTargetAccount(cloudAccount) && this.bootstrapTask?.status) {
                return this.bootstrapTask.status
            }

            if (cloudAccount?.tcid) {
                return 'Linked'
            }

            return '-'
        },
        cloudAccountStatusText(cloudAccount) {
            const status = this.cloudAccountStatus(cloudAccount)
            // Only annotate the stage when the backend bootstrap state drives the chip (not delete tasks).
            const stage = status === this.serverBootstrapStatus(cloudAccount) ? this.currentBootstrapStage(cloudAccount) : null
            if (!stage) {
                return status
            }
            const stageText = `${stage.number}/${stage.total} ${stage.label}`
            return status === 'Failed' ? `Failed at ${stageText}` : `${status} · ${stageText}`
        },
        shouldShowLongTaskTooltip(cloudAccount) {
            const status = this.cloudAccountStatus(cloudAccount)
            return (
                this.isDeleteTaskInProgress(cloudAccount) ||
                status === 'Scheduled' ||
                status === 'Linking' ||
                this.serverBootstrapStatus(cloudAccount) === 'Failed'
            )
        },
        longTaskTooltipText(cloudAccount) {
            if (this.isDeleteTaskInProgress(cloudAccount) || this.cloudAccountStatus(cloudAccount) === 'Deleting') {
                return 'Integration deletion can take up to 15 minutes.'
            }
            if (this.serverBootstrapStatus(cloudAccount) === 'Failed') {
                return 'Use Reconcile to resume the setup from the failed stage.'
            }
            return 'Integration creation can take up to 15 minutes.'
        },
        isCloudAccountStatusRunning(cloudAccount) {
            if (this.isDeleteTaskInProgress(cloudAccount)) {
                return false
            }
            return ['Scheduled', 'Linking'].includes(this.cloudAccountStatus(cloudAccount))
        },
        cloudAccountStatusColor(cloudAccount) {
            const status = this.cloudAccountStatus(cloudAccount)
            if (status === 'Scheduled') return 'secondary'
            if (status === 'Deleting') return 'warning'
            if (status === 'Linking') return 'info'
            if (status === 'Linked') return 'success'
            if (status === 'Failed') return 'error'
            return 'secondary'
        },
        async trackDeleteTask(cloudAccount, taskId) {
            const taskKey = String(cloudAccount.tcid)
            try {
                const taskResult = await fetchTask(taskId)
                if (!this.deleteTasks[taskKey] || String(this.deleteTasks[taskKey].taskId) !== String(taskId)) {
                    return
                }

                if (taskResult instanceof Error) {
                    const failureMessage = this.bootstrapErrorMessage(taskResult, 'Integration deletion failed.')
                    this.$set(this.deleteTasks, taskKey, {
                        ...this.deleteTasks[taskKey],
                        status: 'Failed',
                        result: failureMessage
                    })
                    this.error = failureMessage
                    return
                }

                const completedSuccessfully = this.taskCompletedSuccessfully(taskResult)
                if (!completedSuccessfully) {
                    const failureMessage = this.bootstrapErrorMessage(taskResult?.data, 'Integration deletion failed.')
                    this.$set(this.deleteTasks, taskKey, {
                        ...this.deleteTasks[taskKey],
                        status: 'Failed',
                        result: failureMessage
                    })
                    this.error = failureMessage
                    return
                }

                await this.fetchCloudAccount()
                this.cloudAccounts = this.cloudAccounts.filter(account => String(account?.tcid) !== taskKey)
                this.$delete(this.deleteTasks, taskKey)
                this.notify('Integration deletion completed.')
            } catch (error) {
                const failureMessage = this.bootstrapErrorMessage(error, 'Integration deletion failed.')
                if (this.deleteTasks[taskKey] && String(this.deleteTasks[taskKey].taskId) === String(taskId)) {
                    this.$set(this.deleteTasks, taskKey, {
                        ...this.deleteTasks[taskKey],
                        status: 'Failed',
                        result: failureMessage
                    })
                }
                this.error = failureMessage
            }
        },
        async trackBootstrapTask(taskId) {
            try {
                const taskResult = await fetchTask(taskId)
                if (!this.bootstrapTask || String(this.bootstrapTask.taskId) !== String(taskId)) {
                    return
                }

                if (taskResult instanceof Error) {
                    const failureMessage = this.bootstrapErrorMessage(taskResult, 'Could not fetch cluster bootstrap status.')
                    this.bootstrapTask = {
                        ...this.bootstrapTask,
                        status: 'Failed',
                        result: failureMessage
                    }
                    this.error = failureMessage
                } else {
                    await this.fetchCloudAccount()
                    const hasTcid = this.cloudAccounts.some(cloudAccount => Boolean(cloudAccount?.tcid))
                    const failureMessage = this.bootstrapErrorMessage(taskResult?.data, 'Integration creation failed.')

                    this.bootstrapTask = {
                        ...this.bootstrapTask,
                        status: hasTcid ? 'Linked' : 'Failed',
                        result: hasTcid ? null : failureMessage
                    }

                    if (hasTcid) {
                        this.notify('Integration bootstrap completed.')
                    } else {
                        this.error = failureMessage
                    }
                }
            } catch (error) {
                const failureMessage = this.bootstrapErrorMessage(error, 'Could not fetch cluster bootstrap status.')
                if (this.bootstrapTask && String(this.bootstrapTask.taskId) === String(taskId)) {
                    this.bootstrapTask = {
                        ...this.bootstrapTask,
                        status: 'Failed',
                        result: failureMessage
                    }
                }
                this.error = failureMessage
            }
        },
        notify(text, icon = 'check_circle') {
            this.$store.dispatch('showSnackBar', { snackBarText: text, snackBarTimeout: 5000, snackBarIcon: icon })
        },
        normalizeCloudProviders(cloudProvidersResponse) {
            if (Array.isArray(cloudProvidersResponse)) {
                return cloudProvidersResponse.map(provider => ({
                    cloud_provider: provider.cloud_provider,
                    display_label: provider.display_label || provider.cloud_provider,
                    regions: Array.isArray(provider.regions) ? provider.regions : []
                }))
            }

            if (cloudProvidersResponse && typeof cloudProvidersResponse === 'object') {
                return Object.entries(cloudProvidersResponse).map(([cloudProvider, regions]) => ({
                    cloud_provider: cloudProvider,
                    display_label: cloudProvider,
                    regions: Array.isArray(regions) ? regions : []
                }))
            }

            return []
        },
        normalizeTenantClusters(tenantClusterResponse) {
            if (Array.isArray(tenantClusterResponse)) {
                return tenantClusterResponse
            }

            if (tenantClusterResponse && typeof tenantClusterResponse === 'object') {
                return [tenantClusterResponse]
            }

            return []
        },
        async fetchCloudProviders() {
            this.loadingCloudProviders = true
            try {
                const { data } = await this.$axios.get('/tenant_clusters/cloud_providers')
                this.cloudProviders = this.normalizeCloudProviders(data?.cloud_providers)
            } catch (error) {
                this.cloudProviders = []
                this.error = error.response?.data?.debug || 'Could not load providers.'
            } finally {
                this.loadingCloudProviders = false
            }
        },
        async fetchCloudAccount({ background = false } = {}) {
            if (!this.resourcePoolId) return
            if (!background) {
                this.loading = true
                this.error = ''
            }
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.resourcePoolId}/tenant_cluster`)
                this.cloudAccounts = this.normalizeTenantClusters(data)
            } catch (error) {
                if (error.response?.status === 404) {
                    this.cloudAccounts = []
                    return
                }
                this.error = error.response?.data?.debug || 'Could not load integration.'
            } finally {
                if (!background) {
                    this.loading = false
                }
            }
        },
        openBootstrapDialog() {
            this.bootstrapSettings = {
                cloudProvider: null,
                cloudRegion: null
            }
            this.bootstrapCredentials = { id: '', secret: '' }
            this.bootstrapDialog = true
            this.fetchCloudProviders()
        },
        canUpdateCredentials(cloudAccount) {
            const creatorUcid = cloudAccount?.creator_ucid
            const currentUcid = this.$store?.state?.userInfo?.ucid
            if (creatorUcid == null || currentUcid == null) return false
            return Number.isFinite(Number(creatorUcid)) && Number.isFinite(Number(currentUcid)) && Number(creatorUcid) === Number(currentUcid)
        },
        credentialsExpirationDate(cloudAccount) {
            const raw = cloudAccount?.cloud_credentials_expiration
            if (!raw) return null
            // The backend stores these timestamps as UTC-naive values.
            const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)
            const date = new Date(hasTimezone ? raw : `${raw}Z`)
            return Number.isNaN(date.getTime()) ? null : date
        },
        isCloudCredentialsExpired(cloudAccount) {
            const expiration = this.credentialsExpirationDate(cloudAccount)
            return expiration !== null && expiration.getTime() <= this.nowMs
        },
        formatCredentialsExpiration(cloudAccount) {
            const date = this.credentialsExpirationDate(cloudAccount)
            if (!date) return '–'
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },
        openCredentialsDialog(cloudAccount) {
            this.credentialsTarget = cloudAccount
            this.credentialsForm = { id: '', secret: '' }
            this.credentialsDialog = true
        },
        closeCredentialsDialog() {
            this.credentialsDialog = false
            this.credentialsTarget = null
            this.credentialsForm = { id: '', secret: '' }
            this.$refs.credentialsForm?.resetValidation()
        },
        async updateCredentials() {
            if (!this.$refs.credentialsForm || !this.$refs.credentialsForm.validate()) return
            if (!this.credentialsTarget?.tcid) {
                this.error = 'No integration selected for credential update.'
                return
            }
            this.updatingCredentials = true
            this.error = ''
            try {
                await this.$axios.post(`/resource_pools/${this.resourcePoolId}/tenant_clusters/${this.credentialsTarget.tcid}/credentials`, {
                    os_app_credential_id: this.credentialsForm.id.trim(),
                    os_app_credential_secret: this.credentialsForm.secret
                })
                this.closeCredentialsDialog()
                this.notify('Credentials updated.')
                await this.fetchCloudAccount()
            } catch (error) {
                this.error = this.credentialErrorMessage(error)
            } finally {
                this.updatingCredentials = false
            }
        },
        closeBootstrapDialog() {
            this.bootstrapDialog = false
            this.$refs.bootstrapForm?.resetValidation()
        },
        async bootstrapCloudCluster() {
            if (!this.$refs.bootstrapForm || !this.$refs.bootstrapForm.validate()) return
            this.bootstrapping = true
            this.error = ''
            try {
                const cloudProvider = this.bootstrapSettings.cloudProvider
                const cloudRegion = this.bootstrapSettings.cloudRegion

                this.bootstrapTask = {
                    taskId: null,
                    cloudProvider,
                    cloudRegion,
                    status: 'Scheduled',
                    result: null
                }

                const { data } = await this.$axios.post(`/resource_pools/${this.resourcePoolId}/tenant_clusters`, {
                    cloud_provider: this.bootstrapSettings.cloudProvider,
                    cloud_region: this.bootstrapSettings.cloudRegion,
                    os_app_credential_id: this.bootstrapCredentials.id.trim(),
                    os_app_credential_secret: this.bootstrapCredentials.secret
                })

                const taskId = data?.tkid
                this.bootstrapTask = {
                    ...this.bootstrapTask,
                    taskId,
                    status: taskId == null ? 'Failed' : 'Linking'
                }

                this.closeBootstrapDialog()
                this.notify('Cluster bootstrap has started.')
                await this.fetchCloudAccount()

                if (taskId == null) {
                    this.bootstrapTask = {
                        ...this.bootstrapTask,
                        status: 'Failed',
                        result: 'Task ID missing from cluster bootstrap response.'
                    }
                    this.error = this.bootstrapTask.result
                } else {
                    this.trackBootstrapTask(taskId)
                }
            } catch (error) {
                const failureMessage = this.bootstrapErrorMessage(error, 'Could not start cluster bootstrap.')
                if (this.bootstrapTask) {
                    this.bootstrapTask = {
                        ...this.bootstrapTask,
                        status: 'Failed',
                        result: failureMessage
                    }
                }
                this.error = failureMessage
            } finally {
                this.bootstrapping = false
            }
        },
        reconcileCloudAccount(cloudAccount) {
            return this.performCloudAccountAction(cloudAccount, 'reconcile', 'post', 'Integration reconciliation has started.')
        },
        async refreshCloudAccountResources(cloudAccount) {
            if (!cloudAccount.tcid) return
            this.actionInProgress = true
            this.error = ''
            try {
                await this.$axios.post(`/resource/${this.resourcePoolId}/tenant_clusters/${cloudAccount.tcid}/resync`)
                this.notify('Resources refreshed.')
            } catch (error) {
                this.error = this.bootstrapErrorMessage(error, 'Could not refresh resources.')
            } finally {
                this.actionInProgress = false
            }
        },
        confirmCloudAccountAction(cloudAccount, action) {
            const actionLabel = action === 'unlink' ? 'unlink' : 'delete'
            this.pendingCloudAccountAction = { cloudAccount, action: actionLabel, method: actionLabel === 'delete' ? 'delete' : 'post' }
            this.$store.dispatch('showGlobalDialog', {
                dialogTitle: `${actionLabel[0].toUpperCase()}${actionLabel.slice(1)} provider`,
                dialogText: `Are you sure you want to ${actionLabel} this provider?`,
                dialogAction: ['accept'],
                hash: this.globalDialogHash
            })
        },
        executePendingCloudAccountAction() {
            if (!this.pendingCloudAccountAction) return
            const { cloudAccount, action, method } = this.pendingCloudAccountAction
            this.pendingCloudAccountAction = null
            return this.performCloudAccountAction(cloudAccount, action, method)
        },
        async performCloudAccountAction(cloudAccount, action, method, successMessage) {
            if (!cloudAccount.tcid) return
            this.actionInProgress = true
            this.error = ''
            const isDelete = action === 'delete'
            const taskKey = String(cloudAccount.tcid)
            try {
                if (isDelete) {
                    this.$set(this.deleteTasks, taskKey, {
                        status: 'Scheduled',
                        taskId: null,
                        result: null
                    })
                }

                const suffix = action === 'reconcile' || action === 'unlink' ? `/${action}` : ''
                const response = await this.$axios[method](`/resource_pools/${this.resourcePoolId}/tenant_clusters/${cloudAccount.tcid}${suffix}`)

                if (isDelete) {
                    const taskId = response?.data?.tkid
                    if (taskId == null) {
                        const failureMessage = 'Task ID missing from delete response.'
                        this.$set(this.deleteTasks, taskKey, {
                            ...this.deleteTasks[taskKey],
                            status: 'Failed',
                            result: failureMessage
                        })
                        this.error = failureMessage
                        return
                    }

                    this.$set(this.deleteTasks, taskKey, {
                        ...this.deleteTasks[taskKey],
                        status: 'Deleting',
                        taskId,
                        result: null
                    })
                    this.notify(successMessage || 'Integration deletion has started.')
                    this.trackDeleteTask(cloudAccount, taskId)
                    return
                }

                this.notify(successMessage || `Integration ${action} has started.`)
                await this.fetchCloudAccount()
            } catch (error) {
                if (isDelete) {
                    const failureMessage = this.bootstrapErrorMessage(error, 'Could not delete integration.')
                    this.$set(this.deleteTasks, taskKey, {
                        ...this.deleteTasks[taskKey],
                        status: 'Failed',
                        result: failureMessage
                    })
                    this.error = failureMessage
                    return
                }
                this.error = this.bootstrapErrorMessage(error, `Could not ${action} integration.`)
            } finally {
                this.actionInProgress = false
            }
        }
    },
    watch: {
        resourcePoolId: {
            immediate: true,
            handler() {
                this.fetchCloudAccount()
            }
        },
        shouldPollCloudAccounts(polling) {
            clearInterval(this.bootstrapPollTimer)
            this.bootstrapPollTimer = polling ? setInterval(() => this.fetchCloudAccount({ background: true }), BOOTSTRAP_POLL_INTERVAL_MS) : null
        },
        'bootstrapSettings.cloudProvider'(newCloudProvider) {
            if (!newCloudProvider) {
                this.bootstrapSettings.cloudRegion = null
                return
            }
            if (!this.cloudRegionOptions.includes(this.bootstrapSettings.cloudRegion)) {
                this.bootstrapSettings.cloudRegion = null
            }
        }
    }
}
</script>

<style scoped>
.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: currentColor;
    display: inline-block;
}
</style>
